import React from 'react';
import { useAuthState, useAuthActions, constants } from '@/utils/auth';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { Field as FormikField, FieldProps, Form, Formik } from 'formik';
import * as Yup from 'yup';
import { Button } from '@/components/ui/button';
import { cn } from '@/utils/utils';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Field, FieldError, FieldLabel } from '@/components/ui/field';
import { ConnectedFieldError } from '@/components/Inputs/ConnectedFieldError';

const initialSchema = Yup.object().shape({
  username: Yup.string().required('Ingresa tu DNI'),
});
const fullSchema = Yup.object().shape({
  username: Yup.string().required('Ingresa tu DNI'),
  code: Yup.string()
    .required('Ingresa el código de confirmación que enviamos a tu email')
    .matches(
      /^\d{6}$/,
      'El código ingresado no es válido, asegurate de no incluir espacios antes o después del código'
    ),
  new_password: Yup.string()
    .required('Ingresa tu nueva contraseña')
    .matches(
      /^(?=.*[a-z]).{8,}$/,
      'El password debe consistir de al menos 8 caracteres, y contener una letra minúscula'
    ),
});

const AuthResetPasswordForm = () => {
  const { error, matches, loading, forgotPasswordDelivery } = useAuthState();
  const { forgotPassword, signIn } = useAuthActions();
  const emailSent = React.useMemo(
    () => matches({ [constants.STATE_FORGOT_PASSWORD]: 'choose_password' }),
    [matches]
  );
  const schema = emailSent ? fullSchema : initialSchema;
  return (
    <Formik
      initialValues={{
        username: '',
        code: '',
        new_password: '',
      }}
      enableReinitialize
      validationSchema={schema}
      onSubmit={(values) => {
        forgotPassword(values.username, values.code, values.new_password);
      }}
    >
      {({ values }) => (
        <div className={cn('flex flex-col gap-6')}>
          <Card>
            <CardHeader className="text-center">
              <CardDescription>
                {emailSent
                  ? `Revisa tu correo ${
                      forgotPasswordDelivery.Destination
                        ? `(${forgotPasswordDelivery.Destination})`
                        : ''
                    } para obtener el codigo de verificación.`
                  : 'Ingresa tu email o DNI para recuperar tu contraseña.'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Form className="flex flex-col gap-6">
                <FormikField name="username">
                  {({ field, meta }: FieldProps) => (
                    <Field data-invalid={!!meta.error && meta.touched}>
                      <FieldLabel htmlFor={field.name}>DNI / Email</FieldLabel>
                      <Input
                        {...field}
                        id={field.name}
                        aria-invalid={
                          (!!meta.error && meta.touched) || undefined
                        }
                        type="text"
                        autoCapitalize={'off'}
                        autoCorrect="off"
                        spellCheck="false"
                        autoComplete="username"
                      />
                      <ConnectedFieldError name={field.name} />
                    </Field>
                  )}
                </FormikField>
                {emailSent && (
                  <>
                    <FormikField name="code">
                      {({ field, meta }: FieldProps) => (
                        <Field data-invalid={!!meta.error && meta.touched}>
                          <FieldLabel htmlFor={field.name}>Código</FieldLabel>
                          <Input
                            {...field}
                            id={field.name}
                            aria-invalid={
                              (!!meta.error && meta.touched) || undefined
                            }
                            type="text"
                          />
                          <ConnectedFieldError name={field.name} />
                        </Field>
                      )}
                    </FormikField>
                    <FormikField name="new_password">
                      {({ field, meta }: FieldProps) => (
                        <Field data-invalid={!!meta.error && meta.touched}>
                          <FieldLabel htmlFor={field.name}>
                            Nueva Contraseña
                          </FieldLabel>
                          <Input
                            {...field}
                            id={field.name}
                            aria-invalid={
                              (!!meta.error && meta.touched) || undefined
                            }
                            type="password"
                            autoComplete="new-password"
                          />
                          <ConnectedFieldError name={field.name} />
                        </Field>
                      )}
                    </FormikField>
                  </>
                )}
                {error && !loading && (
                  <FieldError role="alert" className="block">
                    {(error as any).code === 'UserNotFoundException' &&
                    values.username.includes('@')
                      ? 'No encontramos una cuenta con ese email verificado. Si te registraste con DNI y contraseña y todavía no confirmaste tu email, ingresá tu DNI y te reenviamos el mail de confirmación. Si te registraste con tu cuenta de Google, volvé a la pantalla de login y entrá con el botón de Google.'
                      : (error as any).code === 'UserNotFoundException'
                        ? 'Tu email no se encontró en nuestra base de datos, seguro que lo escribiste bien? Si te registraste con tu cuenta de Google no vas a poder resetear tu contraseña de esta forma, volvé a la pantalla de login y entrá con tu cuenta de Google usando el botón correspondiente.'
                        : (error as any).code === 'InvalidParameterException'
                          ? 'Nuestros registros indican que tu email no está confirmado. Por favor, revisa tu email para confirmar tu cuenta antes de intentar restablecer tu contraseña, o comunicate con nosotros para más información.'
                          : (error as any).code === 'CodeMismatchException'
                            ? 'El código ingresado no es válido, asegurate de no incluir espacios antes o después del código'
                            : (error as any).code ===
                                'UserLambdaValidationException'
                              ? error.message.replace(
                                  /^\s*CustomMessage failed with error\s*/,
                                  ''
                                )
                              : error.message}
                  </FieldError>
                )}
                <hr />
                <Button
                  type="submit"
                  className={cn('w-full')}
                  disabled={loading}
                >
                  {loading && <LoadingSpinner />}
                  {!loading && 'Enviar'}
                </Button>
                <Button variant="link" onClick={signIn as any}>
                  Cancelar
                </Button>
              </Form>
            </CardContent>
          </Card>
        </div>
      )}
    </Formik>
  );
};

export default AuthResetPasswordForm;
